import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { SEO_LANGS } from '../data/regions';
import { loadLanguage } from '../../i18n';

const LANG_STORAGE_KEY = 'app-language';

const languages = [
  { code: 'kg', label: 'Кыргызча', flag: '🇰🇬' },
  { code: 'kz', label: 'Қазақша', flag: '🇰🇿' },
  { code: 'uz', label: "O'zbekcha", flag: '🇺🇿' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
];

// /nedvizhimost/... hub pages are language-addressed by URL prefix (see
// src/app/pages/RealEstateHubPage.tsx) — the language switcher needs to
// navigate to the matching URL there, not just flip global i18n state,
// otherwise the hub page's own effect immediately reverts the switch back
// to whatever its URL says and the dropdown looks broken/unresponsive.
function hubPageRestPath(pathname: string): string | null {
  for (const lang of SEO_LANGS) {
    const prefix = lang.urlPrefix ? `/${lang.urlPrefix}/nedvizhimost` : '/nedvizhimost';
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return pathname.slice(prefix.length); // '' or '/bishkek/kvartiry' etc.
    }
  }
  return null;
}

/**
 * Shared language-change logic — hub-page URL rewrite + i18n switch +
 * persisted choice. Used by every language control so the hub-page quirk
 * only lives in one place.
 */
function useLanguageChange() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // On mount, apply the saved language if it differs from the current one.
  useEffect(() => {
    const saved = localStorage.getItem(LANG_STORAGE_KEY);
    if (saved && saved !== i18n.language) {
      loadLanguage(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const change = useCallback(
    (lang: string) => {
      const rest = hubPageRestPath(location.pathname);
      if (rest !== null) {
        const target = SEO_LANGS.find((l) => l.code === lang);
        const newPath = target?.urlPrefix
          ? `/${target.urlPrefix}/nedvizhimost${rest}`
          : `/nedvizhimost${rest}`;
        navigate(newPath);
      }
      loadLanguage(lang);
      localStorage.setItem(LANG_STORAGE_KEY, lang);
      try {
        localStorage.setItem('lang', lang);
      } catch {
        /* ignore */
      }
    },
    [location.pathname, navigate],
  );

  return { current: i18n.language, change };
}

/** Full-width select — used in the desktop bar and the mobile drawer. */
export function LanguageSelect() {
  const { current, change } = useLanguageChange();

  return (
    <div className="w-full">
      <div className="relative">
        <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        <select
          value={current}
          onChange={(e) => change(e.target.value)}
          aria-label="Язык / Тил"
          className="
            w-full h-14
            appearance-none
            pl-12 pr-12
            text-base font-medium text-gray-900
            bg-transparent
            border-none
            focus:outline-none
            focus:ring-0
            transition
            cursor-pointer
          "
        >
          {languages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.flag} {lang.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

/**
 * Compact globe button + popover list — sits next to the mobile menu
 * button in the header so language is one tap away instead of buried in
 * the drawer.
 */
export function LanguageIconMenu() {
  const { current, change } = useLanguageChange();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const activeFlag =
    languages.find((l) => l.code === current)?.flag ?? '🌐';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Язык / Тил"
        aria-haspopup="menu"
        aria-expanded={open}
        className="relative w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 active:scale-95 transition-transform"
      >
        <Globe className="w-[22px] h-[22px] text-gray-700" />
        <span className="absolute -bottom-0.5 -right-0.5 text-[13px] leading-none drop-shadow-sm">
          {activeFlag}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-44 max-h-[70vh] overflow-y-auto rounded-2xl bg-white shadow-xl border border-gray-100 p-1.5 z-[100] animate-in fade-in zoom-in-95"
        >
          {languages.map((lang) => {
            const isActive = lang.code === current;
            return (
              <button
                key={lang.code}
                role="menuitem"
                onClick={() => {
                  change(lang.code);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-left transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 font-semibold'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="text-base leading-none">{lang.flag}</span>
                <span className="flex-1">{lang.label}</span>
                {isActive && <Check size={15} className="text-blue-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
